<template>
    <div class="unit-settings">
        <Card>
            <Heading
                :labelTitle="$t.__('settings.units.unit_settings.title')"
                variant="h2"
                :prefixColoredIcon="{ icon: 'tool', color: 'brand' }"
            />
            <div class="unit-settings__row">
                <CompassFormItem :label="$t.__('settings.units.unit_settings.unit_column.label')">
                    <CompassSkeleton v-if="isLoadingUnitSettings" size="x-large" />
                    <CompassRadioGroup v-else v-model="unitColumn" label="" direction="horizontal">
                        <CompassRadio
                            v-for="item in Object.keys(unitColumns)"
                            :key="unitColumns[item].value"
                            :label="unitColumns[item].label"
                            :modelValue="unitColumns[item].value"
                        />
                    </CompassRadioGroup>
                </CompassFormItem>
                <CompassFormItem
                    :label="$t.__('settings.units.unit_settings.unit_precision.label')"
                    class="unit-precision"
                >
                    <CompassSkeleton v-if="isLoadingUnitSettings" size="x-large" />
                    <CompassSelect v-else v-model="unitAmountPrecision">
                        <CompassDropdownOption
                            v-for="unitAmount in unitAmountPrecisionList"
                            :key="unitAmount"
                            :label="formatPrecision(unitAmount)"
                            :value="unitAmount"
                        />
                    </CompassSelect>
                </CompassFormItem>
            </div>
            <h2 class="unit-settings__card-title typographies-heading-h2">
                {{ $t.__('settings.units.measurement_units.title') }}
            </h2>
            <p class="typographies-paragraph-medium-regular">
                {{ $t.__('settings.units.measurement_units.description') }}
            </p>
            <div class="unit-settings__row">
                <CompassFormItem
                    :label="$t.__('settings.units.measurement_units.measurement_units.label')"
                >
                    <CompassSkeleton v-if="isLoadingUnitSettings" size="x-large" />
                    <CompassSelect v-else v-model="dimensionUnit">
                        <CompassDropdownOption
                            v-for="item in Object.keys(dimensionUnits)"
                            :key="dimensionUnits[item].value"
                            :label="dimensionUnits[item].label"
                            :value="dimensionUnits[item].value"
                        />
                    </CompassSelect>
                </CompassFormItem>
                <CompassFormItem
                    :label="$t.__('settings.units.measurement_units.weight_units.label')"
                >
                    <CompassSkeleton v-if="isLoadingUnitSettings" size="x-large" />
                    <CompassSelect v-else v-model="weightUnit">
                        <CompassDropdownOption
                            v-for="item in Object.keys(weightUnits)"
                            :key="weightUnits[item].value"
                            :label="weightUnits[item].label"
                            :value="weightUnits[item].value"
                        />
                    </CompassSelect>
                </CompassFormItem>
            </div>
            <div class="unit-settings__actions">
                <CompassButton
                    :label="$t.__('common.labels.save_changes')"
                    :disabled="isLoadingUnitSettings"
                    :loading="isSavingSettings"
                    @click="handleUnitSettingsSave"
                />
            </div>
        </Card>
        <Card>
            <Heading
                :labelTitle="$t.__('settings.units.unit_list.title')"
                variant="h2"
                :prefixColoredIcon="{ icon: 'list', color: 'brand' }"
            />
            <CompassAlert
                v-if="showUnitCode"
                customIcon="/ressources/logged/img/icon/svg/info-e-invoicing.svg"
                :description="$t.__('settings.units.unit_list.alert.description')"
                type="neutral"
                :action="{
                    label: $t.__('settings.units.unit_list.alert.action'),
                    callback: consultCodeList,
                }"
                actionPosition="bottom"
                :showClose="false"
            />
            <p class="typographies-paragraph-medium-regular">
                {{ $t.__('settings.units.unit_list.description') }}
            </p>
            <CompassTabs :key="languages.length" v-model="currentTab">
                <CompassTabPane
                    :label="$t.__('common.labels.by_default')"
                    name="default"
                    icon="flag-fill"
                    :isError="hasErrors"
                >
                    <TableLoading v-if="isLoading" />
                    <UnitTable
                        v-else
                        :units="editableUnits"
                        :canEditUnit="isAdmin"
                        :isEditable="isEditable"
                        :editMode="editMode"
                        :unit="unit"
                        :showUnitCode="showUnitCode"
                        @update:unit="handleUpdateDefaultUnit"
                        @addUnit="handleAddUnit"
                        @removeUnit="handleRemoveUnit"
                        @sort="handleSortUpdate"
                    />
                </CompassTabPane>
                <CompassTabPane
                    v-for="language in languages"
                    :key="language.id"
                    :label="language.lang"
                    :name="language.lang"
                    :customIcon="getLanguageIcon(language)"
                >
                    <TableLoading v-if="isLoading" />
                    <UnitTranslationTable
                        v-else
                        :units="editableUnits"
                        :isEditable="isEditable"
                        :editMode="editMode"
                        :language="language.id"
                        @sort="handleSortUpdate"
                        @addUnit="handleAddUnit"
                    />
                </CompassTabPane>
            </CompassTabs>
            <div class="unit-settings__actions">
                <CompassButton
                    v-if="!editMode"
                    :label="$t.__('settings.units.unit_list.edit_button')"
                    type="secondary"
                    :disabled="!isAdmin || isLoading"
                    :loading="isLoadingEditMode"
                    @click="startEditMode"
                />
                <template v-else>
                    <CompassPlainButton
                        :label="$t.__('common.actions.cancel')"
                        variant="neutral"
                        :loading="unitListSaving"
                        @click="handleCancelEditMode"
                    />
                    <CompassButton
                        :label="$t.__('common.actions.save')"
                        type="primary"
                        :loading="unitListSaving"
                        @click="handleUnitListSave"
                    />
                </template>
            </div>
        </Card>
    </div>
</template>
<script setup>
import { ref, computed, onMounted, inject } from 'vue';
import { useStore } from 'vuex';
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import { saveUnitSettings, getUnitSettings } from 'api/settings/unitService';
import UnitTable from 'vcomponents/preferences/Units/UnitTable.vue';
import UnitTranslationTable from 'vcomponents/preferences/Units/UnitTranslationTable.vue';
import TableLoading from 'vcomponents/common/table/TableLoading.vue';
import { useUnits } from 'composables/useUnits';

// Injected
const $t = inject('$t');
const $pushToast = inject('$pushToast');

// Vuex store
const store = useStore();

// Unit list composable (for CompassTabs section)
const {
    unit,
    editableUnits,
    languages,
    currentTab,
    editMode,
    isLoading,
    isLoadingEditMode,
    unitListSaving,
    hasErrors,
    isAdmin,
    isEditable,
    showUnitCode,
    getLanguageIcon,
    startEditMode,
    handleAddUnit,
    handleRemoveUnit,
    handleSortUpdate,
    handleUpdateDefaultUnit,
    handleUnitListSave,
    handleCancelEditMode,
    initialize: initializeUnits,
} = useUnits({ $t, $pushToast });

// Unit settings state (specific to this page)
const unitColumn = ref('');
const weightUnit = ref('');
const dimensionUnit = ref('');
const unitAmountPrecision = ref('default');
const isLoadingUnitSettings = ref(false);
const isSavingSettings = ref(false);

// Computed properties for unit settings
const dimensionUnits = computed(() => store.getters['system/getDimensionUnits']);
const unitColumns = computed(() => store.getters['system/getUnitColumns']);
const weightUnits = computed(() => store.getters['system/getWeightUnits']);
const unitAmountPrecisionList = computed(() => store.getters['system/getUnitAmountPrecisionList']);

// Methods
const formatPrecision = (value) => {
    return value === 'default' ? $t.__('common.labels.default_value') : value;
};

const consultCodeList = () => {
    const url =
        'https://help.sellsy.com/fr/articles/12398556-liste-des-codes-officiels-des-unites-pour-la-facturation-electronique';
    window.open(url, '_blank');
};

const handleUnitSettingsSave = () => {
    const options = {
        unit_id: unit.value,
        unit_display_column: unitColumn.value,
        weight_unit: weightUnit.value,
        dimension_unit: dimensionUnit.value,
        unit_price_precision:
            typeof unitAmountPrecision.value === 'string' &&
            unitAmountPrecision.value.toLowerCase() === 'default'
                ? null
                : Number(unitAmountPrecision.value),
    };

    isSavingSettings.value = true;

    saveUnitSettings(options)
        .then(() => {
            $pushToast({
                type: 'success',
                icon: true,
                text: $t.__('common.labels.settings_saved'),
            });
        })
        .catch((error) => {
            $pushToast({
                type: 'error',
                icon: true,
                text: error.response.data.error.message,
            });
        })
        .finally(() => {
            isSavingSettings.value = false;
        });
};

onMounted(async () => {
    isLoadingUnitSettings.value = true;

    // Fetch unit settings (page-specific)
    getUnitSettings()
        .then(({ data }) => {
            unit.value = data.unit_id;
            unitColumn.value = data.unit_display_column;
            weightUnit.value = data.weight_unit;
            dimensionUnit.value = data.dimension_unit;
            unitAmountPrecision.value = data.unit_price_precision ?? 'default';
        })
        .catch(() => {
            $pushToast({
                type: 'error',
                icon: true,
                text: $t.__('settings.units.settings_fetch_error'),
            });
        })
        .finally(() => {
            isLoadingUnitSettings.value = false;
        });

    // Initialize unit list (from composable)
    await initializeUnits();
});
</script>
<style lang="scss" scoped>
.unit-settings {
    &__card-title {
        margin: 0 0 var(--spacing-3);
    }

    p {
        margin: 0 0 var(--spacing-3);
    }

    &__row {
        display: flex;
        gap: var(--spacing-3);
        margin: 0 0 var(--spacing-4);

        :deep() {
            .compass-form-item {
                flex-shrink: 0;
                width: 220px;

                &.unit-precision {
                    width: auto;

                    .compass-select {
                        width: 160px;
                    }
                }
            }
        }
    }

    &__actions {
        display: flex;
        gap: var(--spacing-3);
        justify-content: flex-end;
    }

    :deep() {
        .compass-alert {
            margin: 0 0 var(--spacing-3);
        }

        .compass-tabs {
            margin: var(--spacing-4) 0;
        }
    }
}
</style>
